/**
 * The colour's name, under the cursor, while the cursor is on the swatch.
 *
 * Behaviour is Tippy.js v6's `followCursor: true` (MIT, atomiks/tippyjs)
 * rather than anything invented here: the cursor position is treated as a
 * virtual reference rather than the element's box, the tooltip tracks it on
 * both axes for as long as the pointer is inside the reference element, and it
 * sits at tippy's own default distance for `placement: 'top'`, `offset[1] = 10`.
 * It goes away when the pointer leaves the swatch.
 *
 * Two things this has to get right beyond that:
 *
 * It is itself a popover, so the browser puts it in the top layer. The full
 * palette is a popover too, and nothing outside the top layer can sit above one
 * however large its z-index, so a plain positioned element was being hidden
 * behind the palette exactly when it was most wanted.
 *
 * It moves on `transform` alone, written once per animation frame. Setting
 * `left`/`top` on every `pointermove` made the browser lay the page out again
 * for each event, which is what made the movement look like a low frame rate.
 * The size is measured once when the name changes rather than on every move,
 * so a move costs no layout read either.
 *
 * Where it parts company with tippy is the follow itself. Tippy pins the
 * tooltip to the cursor exactly; here it eases toward it with a 55ms settle,
 * frame-rate independent, so it carries rather than sticks. It is taken
 * straight to the cursor on first appearance, and the loop stops the moment it
 * has caught up rather than running every frame for as long as you hover.
 */
(() => {
  'use strict';

  const SWATCH = '[data-choice-name]';
  const DISTANCE = 10; // tippy's default offset[1]
  const EDGE = 8;

  let tip = null;
  let current = null;
  let width = 0;
  let height = 0;

  /* Where it is drawn, and where it is heading. Kept apart so the position can
     ease toward the cursor instead of being welded to it. A hard 1:1 follow
     tracks every jitter of the pointer, which is what made it feel heavy; a
     short settle reads as something light being carried along. */
  let drawX = 0;
  let drawY = 0;
  let aimX = 0;
  let aimY = 0;
  let frame = null;
  let last = 0;

  const TAU = 55; // ms to settle; small enough that it never feels like lag

  const element = () => {
    if (tip) return tip;
    tip = document.createElement('div');
    tip.className = 'tn-swatch-tip';
    /* Manual: it is driven by the pointer, not by a click, and must not be
       dismissed when something else in the top layer is opened. */
    if ('popover' in tip) tip.popover = 'manual';
    /* The name is already on the swatch for assistive tech, so this is decoration. */
    tip.setAttribute('aria-hidden', 'true');
    document.body.append(tip);
    return tip;
  };

  /** Turns a cursor point into the box position, clamped and flipped as needed. */
  const aimAt = (x, y) => {
    let left = x - width / 2;
    left = Math.min(Math.max(left, EDGE), document.documentElement.clientWidth - width - EDGE);
    /* Above the cursor, and below it when there is no room up there. */
    let top = y - height - DISTANCE;
    if (top < EDGE) top = y + DISTANCE;
    aimX = left;
    aimY = top;
  };

  const write = () => {
    tip.style.transform = `translate3d(${drawX.toFixed(2)}px, ${drawY.toFixed(2)}px, 0)`;
  };

  /* Frame-rate independent easing, so it settles the same on 60Hz and 120Hz. */
  const step = (now) => {
    frame = null;
    if (!current || !tip) return;
    const delta = last ? Math.min(now - last, 64) : 16;
    last = now;
    const ratio = 1 - Math.exp(-delta / TAU);
    drawX += (aimX - drawX) * ratio;
    drawY += (aimY - drawY) * ratio;
    if (Math.abs(aimX - drawX) < 0.15 && Math.abs(aimY - drawY) < 0.15) {
      drawX = aimX;
      drawY = aimY;
      write();
      return; // settled; the next pointer move starts it again
    }
    write();
    frame = requestAnimationFrame(step);
  };

  const track = (x, y) => {
    aimAt(x, y);
    if (frame === null) {
      last = 0;
      frame = requestAnimationFrame(step);
    }
  };

  const hide = () => {
    current = null;
    if (frame !== null) { cancelAnimationFrame(frame); frame = null; }
    if (!tip) return;
    tip.dataset.shown = 'false';
    if (tip.matches?.(':popover-open')) tip.hidePopover();
  };

  document.addEventListener('pointerover', (event) => {
    /* Touch has no hover, and a tooltip that follows a finger is just occlusion. */
    if (event.pointerType === 'touch') return;
    const swatch = event.target.closest?.(SWATCH);
    if (!swatch || swatch === current) return;
    current = swatch;

    const el = element();
    el.textContent = swatch.dataset.choiceName || '';

    /* The native tooltip would otherwise sit on top of this one. The stash is
       deliberately not `data-native-title`: the card CSS hides anything
       carrying that attribute, because it marks Horizon's own title, and using
       it here made a swatch vanish the moment it was hovered. */
    if (swatch.hasAttribute('title')) {
      swatch.dataset.tnTitle = swatch.getAttribute('title');
      swatch.removeAttribute('title');
    }

    if (el.popover && !el.matches(':popover-open')) {
      try { el.showPopover(); } catch { /* already open, or not connected */ }
    }
    el.dataset.shown = 'true';
    /* Measured once per name, so moving costs no layout read. */
    width = el.offsetWidth;
    height = el.offsetHeight;
    /* The first position is taken, not eased: it should appear where the
       cursor is, not glide in from wherever the last swatch was. */
    aimAt(event.clientX, event.clientY);
    drawX = aimX;
    drawY = aimY;
    write();
  });

  document.addEventListener('pointermove', (event) => {
    if (!current) return;
    if (!current.contains(event.target)) return hide();
    track(event.clientX, event.clientY);
  }, { passive: true });

  document.addEventListener('pointerout', (event) => {
    if (current && !current.contains(event.relatedTarget)) hide();
  });

  /* A swatch that is clicked away, scrolled away, or navigated from. */
  addEventListener('scroll', hide, { passive: true, capture: true });
  document.addEventListener('click', hide);
})();
